import { useMemo, useState } from "react";
import { ChevronDown, MoreHorizontal, Pencil, Trash2, UserRoundCog, CalendarClock, Building2, Tag } from "lucide-react";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "./ui/collapsible";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "./ui/responsive-dropdown-menu";
import AssigneeAvatars from "./AssigneeAvatars";
import { formatDueDate } from "./TaskDetailSheet";
import {
  TASK_DOMAIN_LABELS, PRIORITY_LABELS, priorityBadgeClass, taskDisplayStatus, taskStatusDotClass, parseLocalDate,
} from "../lib/tasks";
import { buildTimelineSections, groupTasksByWeek, formatWeekLabel, formatMonthLabel } from "../lib/timeline";

// Timeline view: a projection of the same `filtered` array the List view
// renders (already scoped and filtered by Tasks.jsx; nothing is filtered here).
// Every action reuses the shared TaskDetailSheet via `onView` or the same row
// actions as List's TaskRow: no second detail/edit surface, no new permission.

const MONTH_ABBR_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

// Short date ("23 sept.") for mobile, where the section header already gives
// the month/year context. Keeps formatDueDate's overdue/"Aujourd'hui" wording;
// appends the year only when it differs from the current one. Also used by
// the mobile List cards in Tasks.jsx.
export function formatMobileDue(dueDate, done) {
  const due = formatDueDate(dueDate, done);
  if (!due || due.overdue || due.text === "Aujourd'hui") return due;
  const date = parseLocalDate(dueDate);
  if (!date) return due;
  const short = `${date.getDate()} ${MONTH_ABBR_FR[date.getMonth()]}`;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return { text: sameYear ? short : `${short} ${date.getFullYear()}`, overdue: false };
}

// Two layouts (desktop dense row, mobile stacked card) sharing the same
// computed pieces (badge, prospect link, actions menu) via local consts; one
// side is always `hidden` by breakpoint.
function TaskRow({
  task, resolveAssignee, resolveProspectName, navigate, onView,
  canEdit, canDelete, directory, onToggleDone, onEdit, onDelete, onAddAssignee,
}) {
  const display = taskDisplayStatus(task);
  const due = formatDueDate(task.due_date, task.done);
  const dueMobile = formatMobileDue(task.due_date, task.done);
  const prospectName = task.prospect_id ? resolveProspectName(task.prospect_id) : null;

  const dot = <span className={`w-2 h-2 rounded-full shrink-0 ${taskStatusDotClass(display)}`} />;
  const domainLabel = task.domain ? TASK_DOMAIN_LABELS[task.domain] : "Sans domaine";
  const priorityBadge = (
    <Badge variant="outline" className={`border-transparent text-2xs font-medium shrink-0 ${priorityBadgeClass(task.priority)}`}>
      {PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium}
    </Badge>
  );
  // Real pharmacy name when resolvable (GET /prospects, permission-gated, see
  // Tasks.jsx); otherwise a generic link, never a leak.
  const prospectLink = task.prospect_id && (
    <button
      type="button" onClick={() => navigate(`/prospects/${task.prospect_id}`)}
      className="inline-flex items-center gap-1 text-xs text-jamora hover:underline shrink-0 max-w-[200px] truncate"
    >
      <Building2 size={12} className="shrink-0" /> {prospectName || "Prospect lié"}
    </button>
  );
  const dueEl = due && (
    <div className={`flex items-center gap-1 text-xs shrink-0 ${due.overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
      <CalendarClock size={12} /> {due.text}
    </div>
  );
  const dueMobileEl = dueMobile && (
    <div className={`flex items-center gap-1 text-xs shrink-0 ${dueMobile.overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
      {dueMobile.text}
    </div>
  );
  const actionsMenu = (canEdit || canDelete) && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 shrink-0" data-testid={`timeline-actions-${task.id}`}>
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit && (
          <DropdownMenuItem onClick={() => onEdit(task)}>
            <Pencil size={14} className="mr-2" /> Modifier
          </DropdownMenuItem>
        )}
        {canEdit && directory.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {directory.filter((u) => !(task.assigned_to || []).includes(u.id)).map((u) => (
              <DropdownMenuItem key={u.id} onClick={() => onAddAssignee(task, u.id)}>
                <UserRoundCog size={14} className="mr-2" /> Ajouter {u.name} comme responsable
              </DropdownMenuItem>
            ))}
          </>
        )}
        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600 focus:text-red-600">
              <Trash2 size={14} className="mr-2" /> Supprimer
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div
      data-testid={`timeline-task-${task.id}`}
      className="rounded-lg border border-slate-100 bg-white hover:border-slate-300 hover:bg-slate-50 transition px-3 py-2.5"
    >
      {/* Desktop: dense single-line row */}
      <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {canEdit ? (
          <Checkbox
            checked={task.done} onCheckedChange={() => onToggleDone(task)}
            data-testid={`timeline-toggle-${task.id}`} className="shrink-0"
          />
        ) : (
          <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0" />
        )}
        {dot}
        <button
          type="button" onClick={() => onView(task)} data-testid={`timeline-view-${task.id}`}
          className={`text-sm font-medium text-left hover:underline ${task.done ? "line-through text-slate-400" : "text-slate-900"}`}
        >
          {task.title}
        </button>
        <div className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
          <Tag size={12} /> {domainLabel}
        </div>
        {priorityBadge}
        {prospectLink}
        <div className="ml-auto flex items-center gap-3 shrink-0">
          <AssigneeAvatars userIds={task.assigned_to} resolveUser={resolveAssignee} />
          {dueEl}
          {actionsMenu}
        </div>
      </div>

      {/* Mobile: stacked card (titre, échéance, domaine, priorité, pharmacie,
          responsables, menu). No quick-toggle checkbox: marking done stays one
          tap away via the title -> TaskDetailSheet. */}
      <div className="md:hidden flex flex-col gap-1.5">
        <div className="flex items-start gap-2">
          <span className="mt-1.5">{dot}</span>
          <button
            type="button" onClick={() => onView(task)}
            className={`flex-1 min-w-0 text-sm font-semibold text-left ${task.done ? "line-through text-slate-400" : "text-slate-900"}`}
          >
            {task.title}
          </button>
          {dueMobileEl}
        </div>
        <div className="pl-4 flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span className="truncate">{domainLabel}</span>
          {priorityBadge}
        </div>
        {prospectLink && <div className="pl-4">{prospectLink}</div>}
        <div className="pl-4 flex items-center justify-between gap-2">
          <AssigneeAvatars userIds={task.assigned_to} resolveUser={resolveAssignee} />
          {actionsMenu}
        </div>
      </div>
    </div>
  );
}

const MOBILE_TASK_LIMIT = 3;

// Mobile-only density control: a busy week/month would otherwise be a wall of
// cards on a phone. At md+ the `rest` block always shows (`md:block` beats the
// base `hidden`). Purely presentational: `tasks` is already filtered/grouped.
function TaskList({ tasks, ...rowProps }) {
  const [expanded, setExpanded] = useState(false);
  const visible = tasks.slice(0, MOBILE_TASK_LIMIT);
  const rest = tasks.slice(MOBILE_TASK_LIMIT);
  return (
    <div className="space-y-2">
      {visible.map((t) => <TaskRow key={t.id} task={t} {...rowProps} />)}
      {rest.length > 0 && (
        <>
          <div className={`space-y-2 ${expanded ? "" : "hidden md:block"}`}>
            {rest.map((t) => <TaskRow key={t.id} task={t} {...rowProps} />)}
          </div>
          {!expanded && (
            <button
              type="button" onClick={() => setExpanded(true)}
              className="md:hidden w-full flex items-center justify-center gap-1 text-xs font-medium text-jamora py-1.5 hover:underline"
            >
              Voir les {rest.length} autres tâche{rest.length > 1 ? "s" : ""} <ChevronDown size={12} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// The timeline rail: one continuous vertical line behind every section's
// marker dot, all at the same x-offset (simpler and more robust than a
// per-segment line height). Shifts slightly on mobile to match RailMarker's
// smaller dot.
function Rail({ children }) {
  return (
    <div className="relative">
      <div className="absolute left-1 md:left-2 top-2 bottom-2 w-px bg-slate-200" aria-hidden="true" />
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function RailMarker({ variant }) {
  const cls = {
    // Lighter teal on mobile, where the rail is the main visual structure;
    // desktop keeps the dark jamora fill.
    current: "bg-teal-400 border-teal-400 md:bg-jamora md:border-jamora",
    week: "bg-white border-slate-300",
    month: "bg-slate-300 border-slate-300",
    muted: "bg-slate-100 border-slate-200",
  }[variant];
  return <span className={`absolute left-0 top-1.5 w-3 h-3 md:w-3.5 md:h-3.5 rounded-full border-2 ${cls}`} aria-hidden="true" />;
}

function SectionHeaderRow({ title, mobileTitle, trailingLabel, count, open, testId }) {
  return (
    <CollapsibleTrigger className="w-full py-1" data-testid={testId}>
      {/* Desktop: unchanged single line */}
      <div className="hidden md:flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <Badge variant="outline" className="border-transparent bg-slate-100 text-slate-600 text-2xs font-medium">
          {count} tâche{count > 1 ? "s" : ""}
        </Badge>
        {trailingLabel && <span className="text-xs font-medium text-emerald-600">{trailingLabel}</span>}
        <ChevronDown size={16} className={`ml-auto text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </div>
      {/* Mobile: shorter title (no "Semaine du" prefix), count pinned right
          next to the chevron, trailing label ("Semaine actuelle") on its
          own line instead of competing for the same row's width. */}
      <div className="md:hidden flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900 truncate">{mobileTitle || title}</span>
          <Badge variant="outline" className="ml-auto border-transparent bg-slate-100 text-slate-600 text-2xs font-medium shrink-0">
            {count} tâche{count > 1 ? "s" : ""}
          </Badge>
          <ChevronDown size={16} className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
        </div>
        {trailingLabel && <span className="text-xs font-medium text-emerald-600">{trailingLabel}</span>}
      </div>
    </CollapsibleTrigger>
  );
}

function WeekSection({ week, ...rowProps }) {
  const [open, setOpen] = useState(!week.isPastWeek);
  return (
    <div className="relative pl-4 md:pl-6">
      <RailMarker variant={week.isCurrentWeek ? "current" : "week"} />
      <Collapsible open={open} onOpenChange={setOpen}>
        <SectionHeaderRow
          title={`Semaine du ${formatWeekLabel(week.monday)}`}
          mobileTitle={formatWeekLabel(week.monday)}
          count={week.tasks.length}
          trailingLabel={week.isCurrentWeek ? "Semaine actuelle" : null}
          open={open}
          testId={`timeline-week-${week.key}`}
        />
        <CollapsibleContent className="pt-2 pb-1">
          <TaskList tasks={week.tasks} {...rowProps} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function MonthSection({ month, ...rowProps }) {
  const [open, setOpen] = useState(false);
  // Computed even when closed: the collapsed header's "N semaines" must be the
  // real number of distinct weeks, never an estimate. groupTasksByWeek is a
  // single Map pass over a month-sized array, so this is cheap.
  const weeks = useMemo(() => groupTasksByWeek(month.withDue), [month.withDue]);
  return (
    <div className="relative pl-4 md:pl-6">
      <RailMarker variant="month" />
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full flex items-center gap-2 md:gap-3 py-1" data-testid={`timeline-month-${month.key}`}>
          <span className="text-sm font-semibold text-slate-900">{formatMonthLabel(month.year, month.month)}</span>
          <Badge variant="outline" className="border-transparent bg-slate-100 text-slate-600 text-2xs font-medium">
            {month.count} tâche{month.count > 1 ? "s" : ""}
          </Badge>
          <span className="hidden md:inline text-xs text-slate-400">{weeks.length} semaine{weeks.length > 1 ? "s" : ""}</span>
          <ChevronDown size={16} className={`ml-auto text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2 pb-1 pl-1">
          {weeks.map((w) => (
            <div key={w.monday.getTime()}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{formatWeekLabel(w.monday)}</div>
              <TaskList tasks={w.tasks} {...rowProps} />
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function NoDateSection({ tasks, ...rowProps }) {
  const [open, setOpen] = useState(false);
  if (tasks.length === 0) return null;
  return (
    <div className="relative pl-4 md:pl-6">
      <RailMarker variant="muted" />
      <Collapsible open={open} onOpenChange={setOpen}>
        <SectionHeaderRow title="Sans échéance" count={tasks.length} open={open} testId="timeline-no-date" />
        <CollapsibleContent className="pt-2 pb-1">
          <TaskList tasks={tasks} {...rowProps} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// `tasks` must already be the caller's filtered array (Tasks.jsx's `filtered`,
// the same one the List view renders): this component only groups and sorts.
// `now` defaults to the real clock, matching lib/timeline.js's testable
// contract. `todayMarkerRef` (optional) is attached to the section at "today"
// so Tasks.jsx's "Aujourd'hui" button can scroll it into view.
export default function TaskTimeline({
  tasks, resolveAssignee, resolveProspectName, navigate, onView,
  canEdit, canDelete, directory, onToggleDone, onEdit, onDelete, onAddAssignee,
  now, todayMarkerRef,
}) {
  const { sections, noDateTasks } = useMemo(() => buildTimelineSections(tasks, now || new Date()), [tasks, now]);
  const rowProps = {
    resolveAssignee, resolveProspectName, navigate, onView, canEdit, canDelete, directory, onToggleDone, onEdit, onDelete, onAddAssignee,
  };

  return (
    <div className="px-2 py-3 md:p-4">
      <Rail>
        {sections.map((section) => {
          if (section.type === "today_marker") {
            // No divider on desktop (the current week's marker already carries that
            // meaning); mobile gets a small explicit "Aujourd'hui" mark on the rail,
            // since an invisible anchor reads as too subtle there.
            return (
              <div key={section.key} ref={todayMarkerRef} className="relative pl-4 md:pl-6">
                <span className="md:hidden absolute left-0 top-1 w-3 h-3 rounded-full bg-white border-2 border-jamora" aria-hidden="true" />
                <span className="md:hidden text-xs font-medium text-jamora">Aujourd'hui</span>
              </div>
            );
          }
          if (section.type === "week") return <WeekSection key={section.key} week={section} {...rowProps} />;
          return <MonthSection key={section.key} month={section} {...rowProps} />;
        })}
        <NoDateSection tasks={noDateTasks} {...rowProps} />
      </Rail>
    </div>
  );
}
